import { IS_MEDIA_OSS_ENABLED, MEDIA_OSS_BUILD_FLAG } from '../../config';
import {
  IS_ANDROID, IS_IOS, IS_WEBVIEW_UA, PLATFORM_ENV,
} from './windowEnvironment';

export type ShellNetworkType = 'wifi' | 'cellular' | 'ethernet' | 'none' | 'other';

export type ShellInfo = {
  platform?: 'ios' | 'android' | string;
  shellVersion?: string;
  webviewVersion?: string;
  bundleId?: string;
  deviceModel?: string;
  osVersion?: string;
};

export type ShellLifecycleEvent =
  { type: 'foreground' }
  | { type: 'background' }
  | { type: 'heartbeat' }
  | { type: 'network'; networkType: ShellNetworkType };

export type ShellOpenChatRequest = {
  chatId: string;
  messageId?: number;
};

type ShellBridge = {
  onForeground: () => void;
  onBackground: () => void;
  onNetwork: (networkType: string) => void;
  onHeartbeat: () => void;
  onMemoryWarning: () => void;
  openChat: (payload: unknown) => void;
};

const rawInfo: ShellInfo | undefined = typeof window !== 'undefined'
  // eslint-disable-next-line no-underscore-dangle
  ? (window as any).__SSG_SHELL__
  : undefined;

export type ShellDetectionSource = 'inject' | 'bridge' | 'none';

function normalizeShellPlatform(value: unknown) {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized.includes('android')) return 'android';
  if (normalized.includes('ios') || normalized.includes('iphone') || normalized.includes('ipad')) return 'ios';
  return normalized;
}

function detectInjectedShell(): ShellInfo | undefined {
  if (!rawInfo?.platform) return undefined;
  return { ...rawInfo, platform: normalizeShellPlatform(rawInfo.platform) };
}

function detectBridgeShell(): ShellInfo | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as any;

  if (IS_ANDROID && typeof w.jsBridge?.postMessage === 'function') {
    return { platform: 'android' };
  }

  const handlers = w.webkit?.messageHandlers;
  if (IS_IOS && handlers && (handlers.eventRegister || handlers.eventMessageSent)) {
    return { platform: 'ios' };
  }

  return undefined;
}

const injectedShell = detectInjectedShell();
const bridgeShell = injectedShell ? undefined : detectBridgeShell();

export const SHELL_DETECTION_SOURCE: ShellDetectionSource = injectedShell
  ? 'inject'
  : (bridgeShell ? 'bridge' : 'none');

export const SHELL_INFO: ShellInfo | undefined = injectedShell || bridgeShell;
export const IS_SHELL = Boolean(SHELL_INFO);
export const IS_SHELL_IOS = SHELL_INFO?.platform === 'ios';
export const IS_SHELL_ANDROID = SHELL_INFO?.platform === 'android';

if (typeof document !== 'undefined' && !IS_SHELL) {
  document.documentElement.classList.add('is-web-browser');
}

const listeners = new Set<(event: ShellLifecycleEvent) => void>();

let shellNetworkType: ShellNetworkType | undefined;

function emit(event: ShellLifecycleEvent) {
  listeners.forEach((listener) => {
    listener(event);
  });
}

function normalizeNetworkType(value: string): ShellNetworkType {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'wifi' || normalized === 'cellular' || normalized === 'ethernet' || normalized === 'none') {
    return normalized;
  }
  return 'other';
}

export function subscribeToShellLifecycle(listener: (event: ShellLifecycleEvent) => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getShellNetworkType() {
  return shellNetworkType;
}

const memoryWarningListeners = new Set<NoneToVoidFunction>();

export function subscribeToShellMemoryWarning(listener: NoneToVoidFunction) {
  memoryWarningListeners.add(listener);
  return () => {
    memoryWarningListeners.delete(listener);
  };
}

let isShellBackgrounded = false;

export function getIsShellBackgrounded() {
  return isShellBackgrounded;
}

let openChatHandler: ((request: ShellOpenChatRequest) => void) | undefined;
let pendingOpenChat: ShellOpenChatRequest | undefined;

function parseOpenChatPayload(payload: unknown): ShellOpenChatRequest | undefined {
  let data: any = payload;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return undefined;
    }
  }
  if (!data || typeof data !== 'object') return undefined;

  const chatId = data.chatId ?? data.chat_id;
  if (chatId === undefined || chatId === '') return undefined;

  const rawMessageId = Number(data.messageId ?? data.msg_id ?? data.message_id);
  const messageId = Number.isInteger(rawMessageId) && rawMessageId > 0 ? rawMessageId : undefined;

  return { chatId: String(chatId), messageId };
}

function handleOpenChat(payload: unknown) {
  const request = parseOpenChatPayload(payload);
  if (!request) return;

  if (openChatHandler) {
    openChatHandler(request);
  } else {
    pendingOpenChat = request;
  }
}

export function setShellOpenChatHandler(handler: (request: ShellOpenChatRequest) => void) {
  openChatHandler = handler;
  if (pendingOpenChat) {
    const request = pendingOpenChat;
    pendingOpenChat = undefined;
    handler(request);
  }
}

export function initShellBridge() {
  if (typeof window === 'undefined') return;

  const bridge: ShellBridge = {
    onForeground: () => {
      isShellBackgrounded = false;
      emit({ type: 'foreground' });
    },
    onBackground: () => {
      isShellBackgrounded = true;
      emit({ type: 'background' });
    },
    onNetwork: (networkType: string) => {
      shellNetworkType = normalizeNetworkType(networkType);
      emit({ type: 'network', networkType: shellNetworkType });
    },
    onHeartbeat: () => emit({ type: 'heartbeat' }),
    onMemoryWarning: () => {
      memoryWarningListeners.forEach((listener) => {
        listener();
      });
    },
    openChat: handleOpenChat,
  };

  // eslint-disable-next-line no-underscore-dangle
  (window as any).__ssgShell = bridge;
}

function isForeignWebView() {
  return IS_WEBVIEW_UA || Boolean((window as any).webkit?.messageHandlers);
}

export function getContainerLabel() {
  const platform = PLATFORM_ENV || 'Browser';

  if (IS_SHELL) {
    return `${platform} Shell`;
  }

  return isForeignWebView() ? `${platform} WebView` : platform;
}

const DEVICE_TYPE_UNSPECIFIED = 0;
const DEVICE_TYPE_BY_PLATFORM: Record<string, number> = {
  Android: 1,
  iOS: 2,
  Windows: 7,
  macOS: 8,
  Linux: 9,
};

export function getDeviceType() {
  if (IS_SHELL_ANDROID) return 3;
  if (IS_SHELL_IOS) return 4;
  if (IS_SHELL) return DEVICE_TYPE_UNSPECIFIED;

  if (isForeignWebView()) return DEVICE_TYPE_UNSPECIFIED;

  return DEVICE_TYPE_BY_PLATFORM[PLATFORM_ENV || ''] ?? DEVICE_TYPE_UNSPECIFIED;
}

export function getContainerInfoReason() {
  const parts = [`container:${IS_SHELL ? 'shell' : 'web'}`, `shellSrc:${SHELL_DETECTION_SOURCE}`];
  if (SHELL_INFO?.platform) parts.push(`shellPlatform:${SHELL_INFO.platform}`);
  if (SHELL_INFO?.shellVersion) parts.push(`shellVersion:${SHELL_INFO.shellVersion}`);
  if (SHELL_INFO?.webviewVersion) parts.push(`webview:${SHELL_INFO.webviewVersion}`);
  if (SHELL_INFO?.deviceModel) parts.push(`model:${SHELL_INFO.deviceModel}`);
  if (SHELL_INFO?.osVersion) parts.push(`os:${SHELL_INFO.osVersion}`);
  parts.push(`oss:${IS_MEDIA_OSS_ENABLED ? 1 : 0}`);
  parts.push(MEDIA_OSS_BUILD_FLAG);
  parts.push(`secure:${typeof isSecureContext !== 'undefined' && isSecureContext ? 1 : 0}`);
  parts.push(`uuid:${typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? 1 : 0}`);
  return parts.join(' ');
}
